/* eslint-disable */
import { lazy, Suspense, useState } from "react";
import {
  FileText,
  Download,
  MoreVertical,
  Lock,
  Clock,
  XCircle,
  Globe2,
} from "lucide-react";
import { docsApi, categoryMeta, formatDate, formatFileSize, fetchBlobWithAuth } from "./api";

// Lazy-load the PDF viewer so pdf.js doesn't ship in the main bundle.
const PdfViewer = lazy(() => import("./PdfViewer"));

export default function DocumentCard({ doc, isAdmin, onChange, showCategory = true, variant = "row" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const meta = categoryMeta(doc.category);

  // The status is conveyed by a single compact badge (only when it's not
  // the normal "published" case) plus a left accent — everything else
  // (stats, description, tags) now lives in the preview/detail panel.
  const statusKey =
    doc.status === "REJECTED"
      ? "rejected"
      : doc.status === "PENDING"
      ? "pending"
      : doc.visibility === "PRIVATE"
      ? "private"
      : doc.is_internet_open
      ? "internet"
      : "ok";

  const compactBadge = () => {
    if (doc.visibility === "PRIVATE")
      return (
        <span className="docs-row-badge rb-private">
          <Lock size={10} /> Private
        </span>
      );
    if (doc.status === "PENDING")
      return (
        <span className="docs-row-badge rb-pending">
          <Clock size={10} /> Pending
        </span>
      );
    if (doc.status === "REJECTED")
      return (
        <span className="docs-row-badge rb-rejected">
          <XCircle size={10} /> Rejected
        </span>
      );
    if (doc.is_internet_open)
      return (
        <span className="docs-row-badge rb-internet" title="Published to the public internet">
          <Globe2 size={10} /> Public web
        </span>
      );
    if (doc.internet_open_requested)
      return (
        <span className="docs-row-badge rb-requested" title="Internet publication awaiting admin approval">
          <Globe2 size={10} /> Web requested
        </span>
      );
    return null;
  };

  const handleDownload = async (e) => {
    if (e) e.stopPropagation();
    try {
      const blob = await fetchBlobWithAuth(docsApi.downloadUrl(doc.id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onChange && onChange();
    } catch (err) {
      alert("Download failed");
    }
  };

  const handleDelete = async (e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await docsApi.delete(doc.id);
      onChange && onChange();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleToggleInternetOpen = async (e) => {
    if (e) e.stopPropagation();
    const next = !doc.is_internet_open;
    const msg = next
      ? `Make "${doc.title}" downloadable by anyone on the internet (no login required)?`
      : `Stop publishing "${doc.title}" to the internet?`;
    if (!window.confirm(msg)) return;
    try {
      await docsApi.setInternetOpen(doc.id, next);
      onChange && onChange();
    } catch (err) {
      alert(err.message || "Could not update");
    }
  };

  // The whole row opens the viewer; clicks inside the action group are
  // ignored so download/menu don't also trigger it.
  const openViewer = (e) => {
    if (
      e.target.closest(".docs-row-actions") ||
      e.target.closest("button") ||
      e.target.closest("a")
    ) {
      return;
    }
    setViewerOpen(true);
  };
  const onCardKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setViewerOpen(true);
    }
  };

  const moreMenu = menuOpen && (
    <div className="docs-card-menu-dropdown">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen(false);
          setViewerOpen(true);
        }}
      >
        Preview
      </button>
      <button onClick={handleDownload}>Download</button>
      {isAdmin && doc.status === "PUBLISHED" && (
        <button onClick={handleToggleInternetOpen}>
          {doc.is_internet_open
            ? "Stop publishing to internet"
            : "Publish to internet"}
        </button>
      )}
      <button onClick={handleDelete}>Delete</button>
    </div>
  );

  const viewer = viewerOpen && (
    <Suspense fallback={null}>
      <PdfViewer
        doc={doc}
        isAdmin={isAdmin}
        onClose={() => setViewerOpen(false)}
        onChange={onChange}
      />
    </Suspense>
  );

  if (variant === "tile") {
    return (
      <>
        <div
          className={`docs-tile docs-tile-${statusKey}`}
          role="button"
          tabIndex={0}
          aria-label={`Open ${doc.title}`}
          onClick={openViewer}
          onKeyDown={onCardKey}
        >
          <div className="docs-tile-top">
            <div
              className="docs-tile-icon"
              style={{ color: meta.color, background: meta.bg }}
            >
              <FileText size={22} strokeWidth={1.7} />
            </div>
            <div
              className="docs-row-actions"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="docs-row-act"
                onClick={handleDownload}
                title="Download"
                aria-label="Download"
              >
                <Download size={15} />
              </button>
              <button
                className="docs-row-act"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="More options"
              >
                <MoreVertical size={15} />
              </button>
              {moreMenu}
            </div>
          </div>
          <div className="docs-tile-title" title={doc.title}>
            {doc.title}
          </div>
          <div className="docs-tile-meta">
            {showCategory && (
              <>
                <span style={{ color: meta.color }}>{meta.label}</span>
                <span className="docs-row-dot">·</span>
              </>
            )}
            <span>{doc.file_type}</span>
            <span className="docs-row-dot">·</span>
            <span>{formatFileSize(doc.file_size)}</span>
          </div>
          {compactBadge()}
        </div>
        {viewer}
      </>
    );
  }

  return (
    <>
      <div
        className={`docs-row docs-row-${statusKey}`}
        role="button"
        tabIndex={0}
        aria-label={`Open ${doc.title}`}
        onClick={openViewer}
        onKeyDown={onCardKey}
      >
        <div
          className="docs-row-icon"
          style={{ color: meta.color, background: meta.bg }}
        >
          <FileText size={18} strokeWidth={1.8} />
        </div>

        <div className="docs-row-main">
          <div className="docs-row-title-line">
            <span className="docs-row-title">{doc.title}</span>
            {compactBadge()}
          </div>
          <div className="docs-row-meta">
            {showCategory && (
              <>
                <span className="docs-row-cat" style={{ color: meta.color }}>
                  {meta.label}
                </span>
                <span className="docs-row-dot">·</span>
              </>
            )}
            <span>{doc.file_type}</span>
            <span className="docs-row-dot">·</span>
            <span>{formatFileSize(doc.file_size)}</span>
            <span className="docs-row-dot">·</span>
            <span>{formatDate(doc.uploaded_at)}</span>
          </div>
          {doc.status === "REJECTED" && doc.rejection_reason && (
            <div className="docs-row-reject">
              Rejected: {doc.rejection_reason}
            </div>
          )}
        </div>

        <div className="docs-row-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="docs-row-act"
            onClick={handleDownload}
            title="Download"
            aria-label="Download"
          >
            <Download size={16} />
          </button>
          <button
            className="docs-row-act"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="More options"
          >
            <MoreVertical size={16} />
          </button>
          {moreMenu}
        </div>
      </div>

      {viewer}
    </>
  );
}
